import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { AuthService } from '../../../auth/data-access/auth.service';
import { Router } from '@angular/router';
import { catchError, throwError } from 'rxjs';
import { TenantContextService } from '../../../auth/data-access/tenant-access.service';
import { ROLE_CODES } from '../../../auth/models/auth.model';

const AUTH_ALLOWLIST = [
  '/auth/web-login',
  '/auth/mobile-login',
  // '/auth/update-password',
  '/auth/password-reset',
  '/healthcheck'
];

function isAllowlisted(url: string): boolean {
  try {
    if (/^https?:\/\//i.test(url)) {
      const u = new URL(url);
      url = u.pathname;
    }
  } catch {}
  return AUTH_ALLOWLIST.some(p => url.startsWith(p));
}

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const auth = inject(AuthService);
  const router = inject(Router);
  const tenantCtx = inject(TenantContextService);

  if (isAllowlisted(req.url) || req.method === 'OPTIONS') {
    return next(req);
  }

  if (auth.isTokenExpired(0)) {
    auth.clearAuthAndStorage();
    router.navigateByUrl('/login');
    return throwError(() => new Error('Token expired'));
  }

  const token = auth.tokenComputedSignal();
  const authUser = auth.authUserComputedSignal();
  const isSameOriginApi = !/^https?:\/\//i.test(req.url);

  if (isSameOriginApi) {
    const setHeaders: Record<string, string> = {};

    if (token) {
      setHeaders['Authorization'] = `Bearer ${token}`;
    }

    const roleCode = authUser?.role?.code;
    const isSuperAdmin = roleCode === ROLE_CODES.SUPER_ADMIN;

    const userCompanyId = authUser?.company?._id ?? null;
    const selectedCompanyId = tenantCtx.selectedCompanyId();
    const selectedCourseId = tenantCtx.selectedCourse()?._id;

    const skipTenant = req.headers.has('X-Skip-Tenant');
    if (skipTenant) {
      req = req.clone({ headers: req.headers.delete('X-Skip-Tenant') });
    }

    if (!skipTenant) {
      // company edit
      // SUPER_ADMIN:
      //   - if they picked a company in the UI → use that
      //   - if not → no companyid header (global scope)
      // ADMIN (or others):
      //   - always use their company from token
      let effectiveCompanyId: string | null = null;

      if (isSuperAdmin) {
        // A super admin working inside a course has a company, they just never
        // picked it from the company list. Falling back to the course's own
        // companyId keeps those writes scoped to the tenant they're actually
        // in, rather than sending none and landing on "any company".
        effectiveCompanyId =
          selectedCompanyId ?? tenantCtx.selectedCourse()?.companyId ?? null;
      } else {
        effectiveCompanyId = userCompanyId ?? null;
      }

      if (effectiveCompanyId) {
        setHeaders['companyid'] = effectiveCompanyId;
      }

      // course edit
      // If UI has a selected course, send it for any role
      if (selectedCourseId) {
        setHeaders['courseid'] = selectedCourseId;
      }
    }

    if (Object.keys(setHeaders).length > 0) {
      req = req.clone({ setHeaders });
    }
  }

  return next(req).pipe(
    catchError((err: unknown) => {
      if (err instanceof HttpErrorResponse && err.status === 401 && !isAllowlisted(req.url)) {
        auth.clearAuthAndStorage();
        router.navigateByUrl('/login');
      }
      return throwError(() => err);
    })
  );
};
